import { useEffect, useRef, useState } from 'react';
import { fetchTrackingView, TrackingApiError, type PublicTrackingView } from './apiClient';

export interface TrackingStreamState {
  data: PublicTrackingView | null;
  loading: boolean;
  error: string | null;
  errorStatus: number | null;
  live: boolean;
}

/**
 * Loads the initial tracking snapshot over a normal GET, then upgrades to a
 * live Server-Sent Events connection for real-time updates as the shipment
 * progresses (spec §27: DATABASE -> TRACKING API -> SSE/WEBSOCKET ->
 * CUSTOMER PAGE). Falls back to the last successfully loaded snapshot if
 * the stream drops — EventSource reconnects on its own, so we don't need
 * to hand-roll retry logic.
 */
export function useTrackingStream(code: string, language: string): TrackingStreamState {
  const [data, setData] = useState<PublicTrackingView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [live, setLive] = useState(false);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    setLive(false);

    sourceRef.current?.close();
    sourceRef.current = null;

    fetchTrackingView(code, language)
      .then((view) => {
        if (cancelled) return;
        setData(view);
        setLoading(false);

        const source = new EventSource(`/api/tracking/${encodeURIComponent(code)}/stream?lang=${encodeURIComponent(language)}`);
        sourceRef.current = source;
        source.addEventListener('open', () => !cancelled && setLive(true));
        source.addEventListener('error', () => !cancelled && setLive(false));
        source.addEventListener('snapshot', (event: MessageEvent) => {
          if (cancelled) return;
          try {
            setData(JSON.parse(event.data));
          } catch {
            // Ignore a malformed frame — the next one will correct the view.
          }
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        if (err instanceof TrackingApiError) {
          setErrorStatus(err.status);
          setError(err.message);
        } else {
          setError('network_error');
        }
      });

    return () => {
      cancelled = true;
      sourceRef.current?.close();
      sourceRef.current = null;
    };
  }, [code, language]);

  return { data, loading, error, errorStatus, live };
}
